// IndexedDB-backed queue for offline expense captures. Single store,
// keyed on a client-generated UUID. The capture sheet enqueues a
// payload (and optionally a receipt blob) when offline; the sync
// runner drains the queue when connectivity returns.

import { openDB, type DBSchema, type IDBPDatabase } from "idb";

export type QueueState =
  | "pending" // Captured offline; receipt blob not yet uploaded.
  | "uploading_receipt" // presignAndPut + finalizeDocument in flight.
  | "submitting" // Receipt has documentId; POST /expenses in flight.
  | "synced" // Server returned 200 (transient state — UI fades it in 3s).
  | "failed"; // Exceeded retry budget; user must Retry or Discard.

export type ExpensePayload = {
  propertyId: number;
  phaseId: number;
  accountId: number;
  amountCents: number;
  occurredOn: string;
  vendor: string | null;
  memo: string | null;
  documentKinds: string[];
  matchedEstimateId: number | null;
  paymentMethod: "card" | "checking" | "other" | null;
};

export type QueuedExpense = {
  id: string; // client-side UUID; doubles as the dedupe key (clientRequestId in Phase E)
  payload: ExpensePayload;
  // null = no receipt; "queued" = blob waiting for upload; "uploaded" = documentId we can attach to /expenses.
  receipt:
    | null
    | {
        kind: "queued";
        blob: Blob;
        mimeType: string;
        filename: string;
      }
    | { kind: "uploaded"; documentId: number };
  createdAt: string;
  updatedAt: string;
  attempts: number;
  lastError: string | null;
  state: QueueState;
};

interface CerebellumDb extends DBSchema {
  queuedExpenses: {
    key: string;
    value: QueuedExpense;
  };
}

const DB_NAME = "cerebellum";
const DB_VERSION = 1;
const STORE = "queuedExpenses";

let dbPromise: Promise<IDBPDatabase<CerebellumDb>> | null = null;

function getDb(): Promise<IDBPDatabase<CerebellumDb>> {
  if (!dbPromise) {
    dbPromise = openDB<CerebellumDb>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: "id" });
        }
      },
    });
  }
  return dbPromise;
}

// ── Subscriber pattern so React hooks can re-render on changes.
type Subscriber = () => void;
const subscribers = new Set<Subscriber>();

function notify(): void {
  for (const s of subscribers) s();
}

export function subscribe(fn: Subscriber): () => void {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

// ── Public API.
export function newClientRequestId(): string {
  // crypto.randomUUID is widely supported in modern browsers + Node 19+.
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  // Fallback for older environments (vitest happy-dom etc).
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

export async function enqueue(input: {
  payload: ExpensePayload;
  receipt: QueuedExpense["receipt"];
}): Promise<QueuedExpense> {
  const now = new Date().toISOString();
  const item: QueuedExpense = {
    id: newClientRequestId(),
    payload: input.payload,
    receipt: input.receipt,
    createdAt: now,
    updatedAt: now,
    attempts: 0,
    lastError: null,
    state: "pending",
  };
  const db = await getDb();
  await db.put(STORE, item);
  notify();
  return item;
}

export async function listAll(): Promise<QueuedExpense[]> {
  const db = await getDb();
  const rows = await db.getAll(STORE);
  return rows.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export async function listPending(): Promise<QueuedExpense[]> {
  const all = await listAll();
  return all.filter((q) => q.state !== "synced");
}

export async function getById(id: string): Promise<QueuedExpense | undefined> {
  const db = await getDb();
  return db.get(STORE, id);
}

export async function update(
  id: string,
  patch: Partial<Omit<QueuedExpense, "id">>,
): Promise<QueuedExpense | null> {
  const db = await getDb();
  const existing = await db.get(STORE, id);
  if (!existing) return null;
  const updated: QueuedExpense = {
    ...existing,
    ...patch,
    updatedAt: new Date().toISOString(),
  };
  await db.put(STORE, updated);
  notify();
  return updated;
}

export async function remove(id: string): Promise<void> {
  const db = await getDb();
  await db.delete(STORE, id);
  notify();
}

// Test helper — wipes the store. Not exported via index; called by
// vitest setup to keep tests deterministic.
export async function _clearForTests(): Promise<void> {
  const db = await getDb();
  await db.clear(STORE);
  notify();
}
